import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_BASE_URL } from '../../core/base.service';
import type { VipStatusResponse, RedeemResponse } from './user.service';

/** 后端 API 统一响应包装 */
interface ApiResponse<T> {
  statusCode: number;
  items: T;
  msg: string;
}

@Injectable({ providedIn: 'root' })
export class VipService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true }) || '';

  /** 当前 VIP 状态（内存缓存，不持久化） */
  private readonly _status = signal<VipStatusResponse | null>(null);
  readonly status = this._status.asReadonly();

  /** 状态短缓存：避免壳守卫 + 子守卫同一次导航重复请求；60s 内命中 */
  private static readonly CacheTtlMs = 60_000;
  private _cachedAt = 0;

  /** 是否为「受限广播」态：无会员（含试用到期）但持有一键喊话长期权限 */
  isRestricted(): boolean {
    const s = this._status();
    return !!s && s.canAccess === false && s.broadcastEnabled === true;
  }

  /** 检查 VIP 状态（60s 内复用缓存结果） */
  checkVipStatus(): Observable<VipStatusResponse> {
    const cached = this._status();
    if (cached && Date.now() - this._cachedAt < VipService.CacheTtlMs) {
      return of(cached);
    }

    return this.http
      .get<ApiResponse<VipStatusResponse>>(
        `${this.apiBaseUrl}/user/CheckVipStatus`,
      )
      .pipe(
        map((res) => {
          // 后端非泛型 ResultModel 返回 items 为单个对象
          const item = res?.items;
          if (!item) throw new Error('Invalid VIP status response');
          return item;
        }),
        tap((status) => {
          this._status.set(status);
          this._cachedAt = Date.now();
        }),
      );
  }

  /** 刷新 VIP 状态（静默，不抛异常） */
  refreshStatus(): Observable<VipStatusResponse | null> {
    return this.checkVipStatus().pipe(
      catchError(() => of(null)),
    );
  }

  /** 兑换 VIP */
  redeemCode(code: string): Observable<RedeemResponse> {
    const formData = new FormData();
    formData.append('vipCode', code.trim().toUpperCase());

    return this.http
      .post<ApiResponse<RedeemResponse>>(
        `${this.apiBaseUrl}/user/ExchangeVip`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || '兑换失败');
          }
          const item = res.items;
          if (!item || !item.isVip) {
            throw new Error(res.msg || '兑换失败');
          }
          return item;
        }),
        tap((result) => {
          this._status.set({
            canAccess: true,
            status: result.vipDaysTotal === -1 ? 'permanent' : 'vip',
            vipExpireAt: result.vipExpireAt,
            trialEndAt: null,
            trialDaysLeft: 0,
            isVip: true,
            // 兑换会员后必非受限态；真实喊话权限以 /broadcast/entitlement 为准
            broadcastEnabled: false,
          });
          // 本地构造的状态可能不含完整喊话权限 → 失效短缓存，下次请求走后端权威值
          this._cachedAt = 0;
        }),
      );
  }

  /** 清除缓存（登出时调用） */
  clearCache(): void {
    this._status.set(null);
    this._cachedAt = 0;
  }
}
